"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { AlertCircle } from "lucide-react";
import { parseJSONSafely, formatValueForDisplay } from "@/lib/utils/json-field-utils";
import type { JsonFieldValue } from "@/components/shared/types";

interface JsonFieldDisplayProps {
  value: JsonFieldValue;
  fieldId: string;
  maxItems?: number;
  className?: string;
}

/**
 * Display-only component for JSON fields in table cells
 * Shows formatted key-value pairs in a compact badge format
 * Shows "Invalid JSON" indicator if parsing fails
 */
export function JsonFieldDisplay({
  value,
  fieldId,
  maxItems = 3,
  className = "",
}: JsonFieldDisplayProps) {
  const { pairs, isValid, totalCount } = useMemo(() => {
    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === "") {
      return { pairs: [], isValid: true, totalCount: 0 };
    }

    const parsed = parseJSONSafely(value);

    if (!parsed) {
      return { pairs: [], isValid: false, totalCount: 0 };
    }

    const entries = Object.entries(parsed);
    return {
      pairs: entries.slice(0, maxItems),
      isValid: true,
      totalCount: entries.length,
    };
  }, [value, maxItems]);

  if (!isValid) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <AlertCircle className="h-4 w-4 text-destructive" />
        <span className="text-xs text-destructive font-medium">Invalid JSON</span>
      </div>
    );
  }

  if (pairs.length === 0) {
    return <div className={`text-xs text-muted-foreground italic ${className}`}>Empty</div>;
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {pairs.map(([key, val]) => {
        // Calculate remaining characters for value: 20 total - key length - colon - space
        const maxValueLength = Math.max(5, 20 - key.length - 2);
        return (
          <Badge key={key} variant="secondary" className="text-xs">
            <span className="font-semibold">{key}:</span>
            <span className="ml-1">{formatValueForDisplay(val, maxValueLength)}</span>
          </Badge>
        );
      })}
      {totalCount > maxItems && (
        <Badge variant="outline" className="text-xs">
          +{totalCount - maxItems} more
        </Badge>
      )}
    </div>
  );
}
